import { Link } from "react-router-dom"
import { Separator } from "../ui/separator"
import { Facebook, Instagram, Youtube, Globe, ShoppingBag, Sparkles } from "lucide-react"
import { toast } from "@/hooks/use-toast"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

const Footer = () => {
  const currentYear = new Date().getFullYear()

  const navLinks = [
    { to: "/", label: "Inicio", tooltip: "Volver a la página principal" },
    { to: "/tournaments", label: "Eventos", tooltip: "Explora torneos activos y próximos" },
    { to: "/stores", label: "Tiendas", tooltip: "Encuentra tiendas organizadoras cerca de ti" },
    { to: "/ranking", label: "Ranking", tooltip: "Revisa el ranking por juego y temporada" },
  ]

  const socialLinks = [
    {
      href: "https://facebook.com/mitosyleyendasoficial",
      label: "Facebook",
      tooltip: "Síguenos en Facebook para noticias y torneos",
      icon: Facebook,
      color: "hover:border-blue-500/60 hover:bg-blue-500/10 hover:text-blue-500",
    },
    {
      href: "https://instagram.com/myl_oficial",
      label: "Instagram",
      tooltip: "Fotos y momentos del circuito competitivo",
      icon: Instagram,
      color: "hover:border-pink-500/60 hover:bg-pink-500/10 hover:text-pink-500",
    },
    {
      href: "https://www.youtube.com/@myloficial",
      label: "YouTube",
      tooltip: "Transmisiones en vivo y highlights de torneos",
      icon: Youtube,
      color: "hover:border-red-500/60 hover:bg-red-500/10 hover:text-red-500",
    },
    {
      href: "https://blog.myl.cl/",
      label: "Blog MyL",
      tooltip: "Artículos, decklists y novedades del juego",
      icon: Globe,
      color: "hover:border-emerald-500/60 hover:bg-emerald-500/10 hover:text-emerald-500",
    },
    {
      href: "https://www.casamyl.cl",
      label: "Casa MyL",
      tooltip: "Tienda oficial — cartas, productos y accesorios",
      icon: ShoppingBag,
      color: "hover:border-primary/60 hover:bg-primary/10 hover:text-primary",
    },
  ]

  return (
    <footer className="relative mt-auto overflow-hidden border-t border-border/70 bg-card/65 backdrop-blur-xl">
      <div className="pointer-events-none absolute -left-12 -top-8 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 top-8 h-44 w-44 rounded-full bg-cyan-500/10 blur-3xl" />

      <div className="container mx-auto px-4 py-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <img src="/logo_myl.png" alt="Torneos MyL" className="h-8 w-auto" />
              <span className="text-lg font-bold text-foreground">Torneos MyL</span>
            </div>

            <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              Comunidad competitiva oficial
            </div>

            <p className="max-w-sm text-sm text-muted-foreground">
              Plataforma líder para la gestión y organización de torneos competitivos.
            </p>
          </div>

          {/* Navigation Links */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">Navegación</h3>
            <nav className="flex flex-col space-y-2">
              <TooltipProvider delayDuration={300}>
                {navLinks.map((item) => (
                  <Tooltip key={item.to}>
                    <TooltipTrigger asChild>
                      <Link
                        to={item.to}
                        className="w-fit text-sm text-muted-foreground transition-colors hover:text-primary"
                      >
                        {item.label}
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="text-xs">
                      {item.tooltip}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </TooltipProvider>
            </nav>
          </div>

          {/* Support & Legal */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">Soporte</h3>
            <nav className="flex flex-col space-y-2">
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href="mailto:jo@klu.cl"
                      className="w-fit text-sm text-muted-foreground transition-colors hover:text-primary"
                      onClick={(e) => {
                        e.preventDefault()
                        toast({
                          title: "Información de Contacto",
                          description: "Si tienes dudas o algún problema, escríbenos a jo@klu.cl",
                          variant: "default",
                        })
                      }}
                    >
                      Contacto
                    </a>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="text-xs">
                    ¿Tienes dudas o problemas? Escríbenos y te ayudamos
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href="/terms-conditions"
                      className="w-fit text-sm text-muted-foreground transition-colors hover:text-primary"
                    >
                      Términos y condiciones
                    </a>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="text-xs">
                    Lee las reglas de uso de la plataforma
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/faq"
                      className="w-fit text-sm text-muted-foreground transition-colors hover:text-primary"
                    >
                      Preguntas Frecuentes
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="text-xs">
                    Respuestas a las dudas más comunes de jugadores y organizadores
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </nav>
          </div>
        </div>

        <Separator className="my-6" />

        {/* Bottom Section */}
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="text-center text-sm text-muted-foreground sm:text-left">
            © {currentYear} Mitos y Leyendas. Todos los derechos reservados.
          </div>

          {/* Social Links */}
          <div className="flex items-center gap-2">
            <TooltipProvider delayDuration={300}>
              {socialLinks.map((item) => {
                const Icon = item.icon
                return (
                  <Tooltip key={item.href}>
                    <TooltipTrigger asChild>
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={item.label}
                        className={`group inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border/70 bg-background/55 text-muted-foreground transition-all duration-200 hover:scale-110 hover:shadow-sm ${item.color}`}
                      >
                        <Icon className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                      </a>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[180px] text-center text-xs">
                      <p className="font-medium">{item.label}</p>
                      <p className="text-muted-foreground">{item.tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                )
              })}
            </TooltipProvider>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
